import type { ChangeEvent, FC, FormEvent } from 'preact/compat';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import { Separator, VisuallyHidden } from '@vkontakte/vkui';
import type { EmojiClickData } from 'emoji-picker-react';

import { usePhoneContext } from '../../context/PhoneContext';
import { useVkCaptchaContext } from '../../context/VkCaptchaContext';
import { useWidget } from '../../context/WidgetContext';
import { useUploadFiles } from '../../hooks/useUploadFiles';
import { CheckResult, type HTMLInputEvent } from '../../types/files';
import type { SubmitMessageProps } from '../../types/messages';
import { getFileUploadErrorMessages, getVkIdResponse, hasPassedAttachment, insertText } from '../../utils';

import { FILE_MB_LIMIT, FILES_COUNT_LIMIT } from '../../constants/files';
import { AttachFilesButton } from '../AttachFilesButton';
import { AttachFilesList } from '../AttachFilesList';
import { EmojiPanel } from '../EmojiPanel';
import { Textarea } from '../Textarea';
import { useAutosizeTextArea } from '../Textarea/useAutosizeTextArea';
import { WidgetEditorRightPanel } from '../WidgetEditorRightPanel';

import styles from './styles.module.css';

interface Props {
  onSubmit: (props: SubmitMessageProps) => void;
}

interface QueuedItem {
  type: 'message' | 'file';
  data: string | File[];
}

export const WidgetEditor: FC<Props> = ({ onSubmit }) => {
  // Получает ошибку или payload в случае автологина по VK ID
  const { payload } = getVkIdResponse();

  const [message, setMessage] = useState('');
  const [isEmojiPanelShown, setIsEmojiPanelShown] = useState(false);

  const { setError, requirePhone, hasMaxUserPhone, locale, localeMessages } = useWidget();

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const queueRef = useRef<QueuedItem[]>([]);
  const isProcessingQueueRef = useRef(false);
  const processQueueCallbackRef = useRef<() => void>();

  const { handleCaptchaOpen, isCaptchaPassed } = useVkCaptchaContext();
  const { setOnPhoneSubmit, setOnPhoneCancel, phoneNumber, showPhoneInput } = usePhoneContext();

  // Ресайз textarea до достижения максимальной высоты
  useAutosizeTextArea(textAreaRef.current, message);

  // Хук для работы с загрузкой файлов
  const { attachmentsData, fileInputRef, handleFilesChange, removeFile, resetUploader, retryUploadFile } =
    useUploadFiles({
      maxFiles: FILES_COUNT_LIMIT,
      maxFileSizeMB: FILE_MB_LIMIT,
      onLimitExceeded: (type) => {
        setError?.(() => getFileUploadErrorMessages(type, localeMessages.errors));
      },
    });

  // Метод изменения текста в поле с сообщением
  const handleChangeEditor = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.currentTarget.value);
  };

  const handleToggleEmojiPanel = useCallback(() => {
    setIsEmojiPanelShown((isShown) => !isShown);
  }, []);

  const handleCloseEmojiPanel = useCallback(() => {
    setIsEmojiPanelShown(false);
  }, []);

  const handleSelectEmoji = useCallback((data: EmojiClickData) => {
    if (textAreaRef.current) {
      setMessage(insertText(textAreaRef.current, data.emoji));
    }
  }, []);

  const handleAttachFiles = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [fileInputRef]);

  // Метод для отправки сообщения после прохождения капчи
  // Текст берём из state (и DOM fallback), а не из FormData — controlled textarea
  const handleVerifiedSubmit = useCallback(
    (text?: string) => {
      const trimmedMessage = (text ?? message ?? textAreaRef.current?.value ?? '').trim();

      if (trimmedMessage !== '' || hasPassedAttachment(attachmentsData)) {
        onSubmit({
          message: trimmedMessage,
          attachments: attachmentsData.filter((file) => file.uploadStatus === CheckResult.Passed),
        });

        resetUploader();
        handleCloseEmojiPanel();
        setMessage('');
      }
    },
    [attachmentsData, handleCloseEmojiPanel, message, onSubmit, resetUploader]
  );

  // Обработчик очереди
  const processQueue = useCallback(() => {
    if (isProcessingQueueRef.current || queueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;
    while (queueRef.current.length > 0) {
      const item = queueRef.current.shift();
      if (!item) {
        continue;
      }

      if (item.type === 'message') {
        handleVerifiedSubmit(item.data as string);
      } else if (item.type === 'file') {
        const mockEvent = {
          target: {
            files: item.data as File[],
          },
        } as unknown as HTMLInputEvent;
        handleFilesChange(mockEvent);
      }
    }

    isProcessingQueueRef.current = false;
  }, [handleFilesChange, handleVerifiedSubmit]);

  // Сохраняем актуальную версию processQueue в ref
  processQueueCallbackRef.current = processQueue;

  // Обработчик отправки формы
  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedMessage = (message || textAreaRef.current?.value || '').trim();

      if (isCaptchaPassed || payload) {
        if (requirePhone && hasMaxUserPhone === false && !phoneNumber) {
          if (trimmedMessage !== '' || hasPassedAttachment(attachmentsData)) {
            queueRef.current.push({
              type: 'message',
              data: trimmedMessage,
            });
          }
          showPhoneInput?.();
          return;
        }
        handleVerifiedSubmit();
      } else {
        // Добавляем сообщение в очередь, чтобы отправить после прохождения капчи
        if (trimmedMessage !== '' || hasPassedAttachment(attachmentsData)) {
          queueRef.current.push({
            type: 'message',
            data: trimmedMessage,
          });
        }
        handleCaptchaOpen();
      }
    },
    [
      attachmentsData,
      handleCaptchaOpen,
      handleVerifiedSubmit,
      isCaptchaPassed,
      message,
      payload,
      requirePhone,
      hasMaxUserPhone,
      phoneNumber,
      showPhoneInput,
    ]
  );

  // Обработчик загрузки файлов
  const handleFileUpload = useCallback(
    (event: HTMLInputEvent) => {
      if (isCaptchaPassed || payload) {
        if (requirePhone && hasMaxUserPhone === false && !phoneNumber) {
          const files = event.target.files ? Array.from(event.target.files) : [];
          if (files.length > 0) {
            queueRef.current.push({
              type: 'file',
              data: files,
            });
          }
          showPhoneInput?.();
          return;
        }
        handleFilesChange(event);
      } else {
        const files = event.target.files ? Array.from(event.target.files) : [];
        if (files.length > 0) {
          queueRef.current.push({
            type: 'file',
            data: files,
          });
        }
        handleCaptchaOpen();
      }
    },
    [
      handleCaptchaOpen,
      handleFilesChange,
      isCaptchaPassed,
      payload,
      requirePhone,
      hasMaxUserPhone,
      phoneNumber,
      showPhoneInput,
    ]
  );

  // Обработка прохождения капчи
  useEffect(() => {
    if (isCaptchaPassed) {
      if (requirePhone) {
        if (hasMaxUserPhone === undefined) {
          return;
        }

        if (!hasMaxUserPhone) {
          setOnPhoneSubmit?.(() => {
            processQueueCallbackRef.current?.();
          });
          setOnPhoneCancel?.(() => {
            queueRef.current = [];
            isProcessingQueueRef.current = false;
          });
          return;
        }
      }

      processQueueCallbackRef.current?.();
    }
  }, [isCaptchaPassed, requirePhone, hasMaxUserPhone, setOnPhoneSubmit, setOnPhoneCancel]);
  // Отправляет сообщение по Enter, по Shift + Enter создает новую строку
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  }, []);

  const showSendButton = message.trim().length > 0 || hasPassedAttachment(attachmentsData);

  return (
    <>
      {isEmojiPanelShown && (
        <EmojiPanel locale={locale} onEmojiSelect={handleSelectEmoji} onCloseOutside={handleCloseEmojiPanel} />
      )}

      {attachmentsData.length > 0 && (
        <AttachFilesList files={attachmentsData} onRemove={removeFile} onRetryFileUpload={retryUploadFile} />
      )}

      <Separator className={styles.widgetPanelSeparator} size={4} />

      <div className={styles.widgetEditorWrapper}>
        <form ref={formRef} id="widgetForm" onSubmit={handleSubmit}>
          <Textarea
            ref={textAreaRef}
            placeholder={localeMessages.chat.placeholder}
            beforeNode={<AttachFilesButton onClick={handleAttachFiles} />}
            afterNode={
              <WidgetEditorRightPanel showSendButton={showSendButton} onToggleEmojiPanel={handleToggleEmojiPanel} />
            }
            onChange={handleChangeEditor}
            onKeyDown={handleKeyDown}
            value={message}
          />

          <VisuallyHidden
            Component="input"
            type="file"
            getRootRef={fileInputRef}
            onChange={handleFileUpload}
            multiple
          />
        </form>
      </div>
    </>
  );
};
